import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";


const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.2
        }
    }
};

export function CallDetailsLoadingSkeleton() {
    return (
        <div className="space-y-4">
            <motion.div
                className="flex items-center justify-between"
                variants={itemVariants}
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        disabled
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-9 w-[120px]" />
            </motion.div>

            {/* Audio Player Skeleton */}
            <motion.div variants={itemVariants}>
                <Card className="p-2">
                    <div className="flex items-center gap-4 h-12">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-8 w-8" /> {/* Back button */}
                            <Skeleton className="h-8 w-8" /> {/* Play button */}
                            <Skeleton className="h-8 w-8" /> {/* Forward button */}
                        </div>
                        <Skeleton className="h-4 w-12" /> {/* Current time */}
                        <Skeleton className="flex-1 h-2" /> {/* Progress bar */}
                        <Skeleton className="h-4 w-12" /> {/* Total time */}
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-8 w-8" /> {/* Volume button */}
                            <Skeleton className="w-24 h-2" /> {/* Volume slider */}
                        </div>
                        <Skeleton className="h-8 w-16" /> {/* Speed selector */}
                    </div>
                </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
                <Tabs defaultValue="overview">
                    <TabsList>
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="conversation">Conversation</TabsTrigger>
                        <TabsTrigger value="transcript">Transcript</TabsTrigger>
                        <TabsTrigger value="analysis">Analysis</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Call Status Card */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-medium">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-24" />
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <div className="flex items-center gap-1">
                                                <Skeleton className="h-5 w-32" />
                                                <Skeleton className="h-4 w-4" />
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>

                            {/* Conversation Details Card */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-medium">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-36" />
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-5 w-16" />
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>

                            {/* Provider Details Card */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-medium">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-32" />
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <div className="flex items-center gap-1">
                                                <Skeleton className="h-5 w-32" />
                                                <Skeleton className="h-4 w-4" />
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Additional Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            {/* Timing Details Card */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-medium">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-28" />
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-4 w-36" />
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>

                            {/* Recording Details Card */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-medium">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-36" />
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <div className="flex items-center gap-1">
                                                <Skeleton className="h-5 w-32" />
                                                <Skeleton className="h-4 w-4" />
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </motion.div>
        </div>
    );
}
